import $ from "jquery";
import _ from "lodash";
import { ButtonSpinner } from "../../ajax/buttonSpinner";
import { dialogs } from "../dialogs";
import { allPopulated } from "../../types";
import { modal } from "../modal";
import { cloneableFactory } from "../cloneable";
import { subscribe } from "../../events/jQuerySubscription";
import { EventEmitter } from "../../events/eventEmitter";
import { getFiles, muteDocumentDragEvent } from "../../technical/files";
class FilePicker extends EventEmitter {
    constructor(picker) {
        super();
        this.files = [];
        this.picker = picker;
        this.fileInput = picker.find("input[type='file']").first();
        this.tempFileNameInput = $(this.picker.data("file-picker-temp-selector"));
        this.originalFileNameInput = $(this.picker.data("file-picker-original-selector"));
        this.url = this.picker.data("file-picker-url");
        this.suppressErrorAlert = this.picker.is("[data-file-picker-suppress-error-alert]");
        this.dropZone = this.picker.closest(this.picker.data("file-picker-dropzone-selector"));
        this.subscriptions = [
            subscribe(this.fileInput, "change", (e) => this.processNewFiles([...(e.target.files || [])])),
            muteDocumentDragEvent("dragover"),
            muteDocumentDragEvent("dragenter"),
            muteDocumentDragEvent("dragleave"),
            muteDocumentDragEvent("drop"),
            subscribe(this.dropZone, "drop", async (e) => {
                var _a;
                e.preventDefault();
                e.stopPropagation();
                const files = await getFiles((_a = e.originalEvent) === null || _a === void 0 ? void 0 : _a.dataTransfer);
                this.processNewFiles(files);
            }),
            subscribe(this.picker, "keydown", (e) => {
                const { key } = e;
                if (key === "Enter" || key === " ") {
                    e.preventDefault();
                    this.fileInput.trigger("click");
                }
            }),
        ];
    }
    cleanUp() {
        this.subscriptions.forEach((s) => s.off());
    }
    processNewFiles(files) {
        if (!_.some(files))
            return;
        this.files = files;
        this.emit("filesAdded", this.files);
        this.handleTempFiles();
    }
    async handleTempFiles() {
        if (!this.url) {
            this.displayFileName();
            return;
        }
        const buttonSpinner = new ButtonSpinner(_.some(this.dropZone) ? this.dropZone : this.fileInput.parent());
        buttonSpinner.show();
        const responses = await Promise.all(this.files.map(async (file) => {
            const body = new FormData();
            body.append("file", file);
            const response = await fetch(this.url, {
                method: "POST",
                body,
            });
            if (response.status === 200)
                return (await response.json());
        }));
        if (!allPopulated(responses)) {
            const message = "There was an unexpected error uploading your file. If you continue to experience problems, please contact support@expensein.com.";
            this.suppressErrorAlert ||
                dialogs.showDialog("alert", {
                    title: "Message",
                    message,
                });
            this.emit("uploadError", message);
            buttonSpinner.hide();
            return;
        }
        const messages = responses
            .filter((r) => !r.Success && r.Message)
            .map((r) => r.Message);
        if (_.some(messages)) {
            const message = messages.join("<br /><br />");
            this.suppressErrorAlert ||
                dialogs.showDialog("alert", {
                    title: "Message",
                    message,
                });
            this.emit("uploadError", message);
        }
        const successful = responses.filter((r) => r.Success && r.Data);
        if (_.some(successful)) {
            this.displayFileName();
            this.tempFileNameInput.val(successful[0].Data.TempFileName);
            this.originalFileNameInput.val(successful[0].Data.OriginalFileName);
            this.emit("tempFilesUploaded", successful.map((r) => r.Data));
        }
        buttonSpinner.hide();
    }
    displayFileName() {
        this.picker.children("[data-file-picker-label]").val(this.files[0].name);
    }
}
class FilePickerFactory {
    initialise(container) {
        const pickerElements = $(container).find("[data-file-picker]");
        pickerElements.toArray().forEach((p) => {
            const pickerElement = $(p);
            if (!pickerElement.data("file-picker-model")) {
                const filePicker = new FilePicker(pickerElement);
                pickerElement.data("file-picker-model", filePicker);
            }
        });
    }
    cleanUp(container) {
        const pickers = $(container).find("[data-file-picker]");
        pickers.toArray().forEach((p) => {
            const model = $(p).data("file-picker-model");
            model && model.cleanUp();
        });
    }
    getModels(pickerElements) {
        return pickerElements
            .toArray()
            .map((p) => {
            const pickerElement = $(p);
            return pickerElement.data("file-picker-model");
        })
            .filter((m) => !!m);
    }
}
export const filePickerFactory = new FilePickerFactory();
modal.on("show", (modal) => filePickerFactory.initialise(modal));
modal.on("hide", (modal) => filePickerFactory.cleanUp(modal));
cloneableFactory.on("itemAdded", (row) => filePickerFactory.initialise(row));
cloneableFactory.on("itemRemoved", (row) => filePickerFactory.cleanUp(row));
$(() => filePickerFactory.initialise(document));
